import { useMemo, useRef, useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { personDisplayLabel } from '@avsuite/shared';
import { apiFetch, NetworkError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { enqueueOp, amendQueuedOp } from '../../lib/offline/opQueue';
import { getBookingsReadStamp, readAllCachedBookings } from '../../lib/offline/cache';
import { stalenessFor } from '../../lib/offline/staleness';
import { C, F, localToUtcIso, fmtTimeInTz, dateKeyInTz, todayInTz } from './theme';
import { seedMaintConfirmation, buildMaintConfirmation } from '../../lib/ops-board/maintConfirmation';
import { ModalOverlay, Field, inputStyle } from './atoms';
import { aircraftStatus } from './panels';
import { useIsMobile } from '../../lib/useIsMobile';
// The submit decision chain, extracted as a pure function so it can be gated
// (database/verify-booking-submit-gate.ts). See that file's header for why this seam and not
// buildPayload. STUDENT_LINE_CAPACITIES / EXTERNAL_AC_LABELS live there too, so the rules and the
// wording they interpolate cannot drift apart.
import {
  evaluateBookingSubmit, EXTERNAL_AC_LABELS, STUDENT_LINE_CAPACITIES, type SubmitInput,
} from '../../lib/ops-board/bookingSubmit';
import {
  DEFAULT_DURATION_MINS, durationOptions, formatDurationLabel, initialDurationMins,
} from '../../lib/ops-board/duration';
import PersonCombobox, { type PersonOption } from './PersonCombobox';
import StudentClientCreateModal from '../admin/StudentClientCreateModal';
import UserFormModal from '../admin/UserFormModal';
import type {
  Aircraft, Asset, Base, Booking, BookingFormSeed, BookingKind, CompanySettings, CollisionDetails, BookingCollision,
  ExternalAircraftSource, Participant, ParticipantCapacity, Staff, Student, TrainingActivity,
} from '../../lib/opsBoardTypes';

interface Props {
  bases: Base[];
  aircraft: Aircraft[];
  assets: Asset[];
  staff: Staff[];
  students: Student[];
  activities: TrainingActivity[];
  bookingKinds: BookingKind[];
  settings: CompanySettings | null;
  defaultBaseId: string;
  prefill?: { baseId?: string; aircraftReg?: string; assetId?: string; pilotId?: string; startTime?: string; date?: string };
  // Copy a booking (recurring/copy Phase 1) — a full create-mode draft built from an existing
  // booking (buildCopyDraft). Wins over `prefill` in create mode; crew + date/time are left empty
  // for the user. Ignored when `editing` is set (edit mode is unchanged).
  seed?: BookingFormSeed | null;
  editing?: Booking | null;
  onClose: () => void;
  // Called after a successful create/edit with the server's advisory warnings + the saved booking.
  onSubmitted: (warnings: string[], booking: Booking | null) => void;
  // PR-000008 — refresh the people reference after an inline student/client/instructor create,
  // so the new person appears in the picker immediately (no manual board reload).
  onPeopleChanged?: () => void;
}

// Recurring bookings (Phase 2) — weekday labels (0=Sun..6=Sat); displayed Mon-first.
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const SETPOS_LABELS: Record<string, string> = { '1': 'first', '2': 'second', '3': 'third', '4': 'fourth', '5': 'fifth', '-1': 'last' };
const MONTH_LABELS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Participant capacities (S30). external_* are people NOT in the system (no user_id).
const CAPACITY_LABELS: Record<ParticipantCapacity, string> = {
  instructor: 'Instructor', student: 'Student', client: 'Client',
  external_instructor: 'External Instructor', external_examiner: 'External Examiner',
};
const CAPACITY_ORDER: ParticipantCapacity[] = ['instructor', 'external_instructor', 'external_examiner', 'student', 'client'];
// Non-instructor (student-line) capacities — a client occupies the same person line as a student.
// Moved to lib/ops-board/bookingSubmit.ts (2026-08-30) so the submit decision chain, which reads it
// for the soft challenge, stays free of this React module. Re-exported nowhere; imported above.
// Fixed options offered in the Instructors picker for people not in the system.
const EXTERNAL_OPTIONS: { value: 'external_instructor' | 'external_examiner'; label: string }[] = [
  { value: 'external_instructor', label: '＋ External Instructor…' },
  { value: 'external_examiner', label: '＋ External Examiner…' },
];

// A participant row: either an INTERNAL person (user_id set, external_name null) or an
// EXTERNAL person (user_id null, external_name set). capacity drives which.
interface PartRow { user_id: string | null; capacity: ParticipantCapacity; external_name: string | null; rank: number | null }

// An external (non-fleet) aircraft row (Phase 1). invoicer is Crosshire-only.
interface ExtAcRow { source: ExternalAircraftSource; type: string; registration: string; invoicer: string }
// EXTERNAL_AC_LABELS also moved to lib/ops-board/bookingSubmit.ts — the "needs a type and a
// registration" message interpolates it, so the wording has ONE home. Imported above.

// Shared chip-row styling for multi-select lists (aircraft, assets, participants).
const chipRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 6px' };
const chipText: CSSProperties = { flex: 1, fontSize: 11, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const chipX: CSSProperties = { background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, color: C.red, cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: '2px 6px', flexShrink: 0 };
// Section divider + header used to group the booking form (Booking assets / Group Booking Settings).
const sectionHdr: CSSProperties = { borderTop: `1px solid ${C.border}`, marginTop: 6, marginBottom: 10, paddingTop: 10, fontSize: 11, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.06em' };

// Staff picker/chip label — "CODE - First Last" (mirrors the backend actorDisplayLabel). Falls back
// to full_name when structured first/last names are absent, and drops the code prefix when a staff
// member has no pilot_code. e.g. "MACKB - Ben Mackney".
const staffLabel = (s: Staff): string => {
  const namePart = [s.first_name, s.last_name].filter(Boolean).join(' ').trim() || s.full_name;
  const code = s.pilot_code?.trim();
  return code ? `${code} - ${namePart}` : namePart;
};

const minutesOf = (t: string): number => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
// Add `n` calendar days to a wall-clock date string (YYYY-MM-DD), returning a YYYY-MM-DD string.
// Uses UTC arithmetic purely as a calendar (no timezone shift): the whole-day payload's midnight
// instants are formed later by localToUtcIso against the base tz.
const addDays = (dateStr: string, n: number): string => {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d + n)).toISOString().slice(0, 10);
};
const timeOf = (mins: number): string => {
  const m = ((mins % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

// PR-000027 clash popup: one visible row per (booking, resource line). The service can return the
// same booking on several lines (aircraft + person, etc.) or the same line twice — dedupe on
// booking_id + line.label so the list reads cleanly.
function dedupeCollisions(cols: BookingCollision[]): BookingCollision[] {
  const seen = new Set<string>();
  return cols.filter((c) => {
    const k = `${c.booking_id}|${c.line.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export default function BookingForm(props: Props) {
  const { bases, aircraft, assets, staff, students, activities, bookingKinds, settings, editing, seed } = props;
  const { user } = useAuth(); // 5d-p2: offline ops are queued per-user
  const isMobile = useIsMobile();
  const isAdmin = (user?.roles ?? []).includes('admin'); // Phase 5: instructor "Create new" is admin-only

  const baseById = useMemo(() => Object.fromEntries(bases.map((b) => [b.id, b])), [bases]);
  const rankAllowed = settings?.participant_hierarchy_mode === 'ranked';

  // Phase 5 inline-create: which create modal is open + the typed text to prefill. Labels for
  // people created mid-booking are stashed here so their just-added chip renders before the next
  // reference refresh.
  const [createFlow, setCreateFlow] = useState<{ kind: 'person' | 'instructor'; prefill?: { first_name?: string; last_name?: string } } | null>(null);
  const [extraLabels, setExtraLabels] = useState<Map<string, string>>(() => new Map());

  // People directory (staff ∪ students ∪ clients) for the participant pickers. Each non-staff
  // entry carries its `kind` so the add-defaults the participant capacity (student→student,
  // client→client); clients render their company via personDisplayLabel.
  const people = useMemo(() => {
    const m = new Map<string, { id: string; label: string; isStaff: boolean; kind?: 'student' | 'client' }>();
    for (const s of staff) m.set(s.id, { id: s.id, label: staffLabel(s), isStaff: true });
    for (const s of students) {
      if (m.has(s.id)) continue;
      const nm = personDisplayLabel({ full_name: s.preferred_name ?? s.full_name, company: s.company });
      const label = s.kind === 'student' && s.training_stage ? `${nm} — ${s.training_stage}` : nm;
      m.set(s.id, { id: s.id, label, isStaff: false, kind: s.kind });
    }
    return m;
  }, [staff, students]);
  const personLabel = (id: string): string => people.get(id)?.label ?? extraLabels.get(id) ?? id;
  // Display label for a participant row — external people show their typed name + capacity.
  const partLabel = (p: PartRow): string =>
    p.user_id ? personLabel(p.user_id) : `${p.external_name ?? '—'} — ${CAPACITY_LABELS[p.capacity]}`;

  // The tenant's standard booking length (CompanySettings.default_booking_mins, 0.12.4).
  // ⚠ `settings` IS NULLABLE AND IS SERVED FROM AN OFFLINE CACHE (offline/prefetch.ts, singleton).
  // A device that has never cached the settings singleton renders this form with settings === null,
  // so the fallback is load-bearing, not decorative. Read once here; every seed branch below uses
  // this one value so Duration mode and Finish mode cannot disagree.
  const defaultBookingMins = settings?.default_booking_mins ?? DEFAULT_DURATION_MINS;

  // ── Initial form state (prefill from an edited booking, a slot click, or defaults) ──
  const init = useMemo(() => {
    if (editing) {
      const tz = editing.dep_timezone;
      const st = editing.block_start_at ? fmtTimeInTz(editing.block_start_at, tz) : '09:00';
      const startDate = editing.block_start_at ? dateKeyInTz(editing.block_start_at, tz) : todayInTz(tz);
      // PR-000057 — the booking's REAL length, verbatim. The option list is an input
      // convenience and must never be applied as a whitelist to data that already exists.
      // ⚠ ONE variable, used by BOTH the duration field and the derived finish time. They used
      // to be computed independently (initialDurationMins here, a bare `?? 60` there), so a
      // change to one silently left the other behind — the 0.12.4 defect, in miniature.
      const durationMins = initialDurationMins(editing.activity_mins, defaultBookingMins);
      return {
        base_id: editing.base_id,
        date: startDate,
        startTime: st,
        durationMins,
        finishDate: editing.arr_time_planned ? dateKeyInTz(editing.arr_time_planned, tz) : startDate,
        finishTime: editing.arr_time_planned ? fmtTimeInTz(editing.arr_time_planned, tz) : timeOf(minutesOf(st) + durationMins),
        activity_id: editing.activity_id ?? '',
        booking_kind_id: editing.booking_kind_id ?? '',
        title: editing.title ?? '',
        notes: editing.activity_note ?? '',
        internal_note: editing.internal_note ?? '',
      };
    }
    // Copy a booking (Phase 1): seed the create form from an existing booking. Carry the setup
    // (base, activity, kind, title, duration, notes) but CLEAR the date/time — the operator must
    // choose the new date (the whole point of a copy). Brief/activity mins are kept so dep/arr
    // recompute once a start is chosen.
    if (seed) {
      // Same single-variable rule as the edit branch above: the finish time is DERIVED from the
      // duration, never recomputed from the source booking.
      const durationMins = initialDurationMins(seed.activity_mins, defaultBookingMins);
      return {
        base_id: seed.base_id,
        date: '',
        startTime: '09:00',
        durationMins,
        finishDate: '',
        finishTime: timeOf(minutesOf('09:00') + durationMins),
        activity_id: seed.activity_id,
        booking_kind_id: seed.booking_kind_id,
        title: seed.title,
        notes: seed.notes,
        internal_note: seed.internal_note,
      };
    }
    const valid = (id?: string) => (id && baseById[id] ? id : undefined);
    // Respect an EXPLICIT prefill base (incl. '' — the ops "pick a base" default, Task B1): do NOT
    // fall back to the board base. Only when no prefill base was provided do we fall back.
    const baseId = props.prefill?.baseId !== undefined
      ? (valid(props.prefill.baseId) ?? '')
      : (valid(props.defaultBaseId) ?? bases[0]?.id ?? '');
    const tz = baseById[baseId]?.timezone ?? 'Australia/Darwin';
    const st = props.prefill?.startTime ?? '09:00';
    // ⚠ THE STANDARD BOOKING LENGTH, and it must reach BOTH modes. Duration mode reads
    // `durationMins`; Finish mode reads `finishTime`. Setting only the first left the same form
    // saying two hours in one mode and one hour in the other — hence one variable, used twice.
    const durationMins = defaultBookingMins;
    return {
      base_id: baseId,
      date: props.prefill?.date ?? todayInTz(tz),
      startTime: st,
      durationMins,
      finishDate: props.prefill?.date ?? todayInTz(tz),
      finishTime: timeOf(minutesOf(st) + durationMins),
      activity_id: '',
      booking_kind_id: '',
      title: '',
      notes: '',
      internal_note: '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState(init);
  // Multi-allocation (S30) — selected aircraft + assets as arrays (create + edit).
  const [aircraftRegs, setAircraftRegs] = useState<string[]>(() => {
    if (editing) return editing.asset_allocations.filter((a) => a.aircraft_registration).map((a) => a.aircraft_registration as string);
    if (seed) return [...seed.aircraftRegs];
    return props.prefill?.aircraftReg ? [props.prefill.aircraftReg] : [];
  });
  const [assetIds, setAssetIds] = useState<string[]>(() => {
    if (editing) return editing.asset_allocations.filter((a) => a.asset_id).map((a) => a.asset_id as string);
    if (seed) return [...seed.assetIds];
    return props.prefill?.assetId ? [props.prefill.assetId] : [];
  });
  // External aircraft (Phase 1) — NOT in the fleet; free text. crosshire allows an invoicer.
  const [externalAc, setExternalAc] = useState<ExtAcRow[]>(() => {
    if (editing) {
      return editing.asset_allocations
        .filter((a) => a.external_aircraft_source)
        .map((a) => ({
          source: a.external_aircraft_source as ExternalAircraftSource,
          type: a.external_aircraft_type ?? '',
          registration: a.external_aircraft_registration ?? '',
          invoicer: a.external_aircraft_invoicer ?? '',
        }));
    }
    if (seed) return seed.externalAc.map((e) => ({ ...e }));
    return [];
  });
  // Participants — initialised from the edited booking (by capacity), or a prefilled instructor.
  const [participants, setParticipants] = useState<PartRow[]>(() => {
    if (editing) return editing.participants.map((p: Participant) => ({ user_id: p.user_id, capacity: p.capacity, external_name: p.external_name, rank: p.rank }));
    if (seed) return []; // Copy: crew is deliberately cleared — the user re-picks (forcing function).
    if (props.prefill?.pilotId) return [{ user_id: props.prefill.pilotId, capacity: 'instructor', external_name: null, rank: null }];
    return [];
  });
  const [maxParticipants, setMaxParticipants] = useState<string>(
    editing?.max_participants != null ? String(editing.max_participants) : (seed?.max_participants ?? ''),
  );
  const [hardLimit, setHardLimit] = useState<boolean>(editing?.participants_limit_hard ?? seed?.participants_limit_hard ?? false);
  // "Always remind the instructor" OVERRIDE. AMENDED 2026-08-25: the checkbox no longer ARMS the
  // reminder — a reminder is sent automatically when the booking has a staff note or an approval
  // outstanding, and this only forces one when there is nothing else to say.
  // A copy does not carry it (BookingFormSeed has no such field): it is a decision about a
  // specific day, and a copy has no date yet.
  const [remindInstructor, setRemindInstructor] = useState<boolean>(editing?.reminder_enabled ?? false);

  const [entryMode, setEntryMode] = useState<'duration' | 'finish'>(() => {
    // Copy: the date/time is cleared, so start in duration mode (dep/arr recompute once a new
    // start is chosen). Edit keeps finish mode for a multi-day booking; create defaults to duration.
    if (seed) return 'duration';
    return editing?.arr_time_planned && editing.block_start_at && dateKeyInTz(editing.arr_time_planned, editing.dep_timezone) !== dateKeyInTz(editing.block_start_at, editing.dep_timezone) ? 'finish' : 'duration';
  });
  // Whole-day booking (email 2026-07-11): a checkbox that makes the booking run 00:00 → next-day
  // 00:00 (true 1440 min), multi-day supported via a user-facing "Last day" date. The midnight
  // maths (finishDate = lastDay + 1) is derived at payload time so the user never sees it.
  const [wholeDay, setWholeDay] = useState(false);
  const [lastDay, setLastDay] = useState<string>(() => init.date);
  // Stash the pre-tick time entry so unticking restores exactly what the user had.
  const wholeDayStash = useRef<{ entryMode: 'duration' | 'finish'; startTime: string; finishTime: string; finishDate: string; durationMins: number } | null>(null);
  // ⚠ MAINTENANCE AND DEFECTS ARE ENTERED AS AN EXPECTED COMPLETION DATE, NEVER A DURATION
  // (Ben, 2026-09-07). A defect has an expected return; it does not have an arbitrary length.
  // Same stash-and-restore shape as wholeDayStash above rather than a second mechanism: holds the
  // mode the user was in before a maintenance kind forced ECD, so changing the kind back restores
  // what they had.
  const maintModeStash = useRef<'duration' | 'finish' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Copy a booking (Phase 1) — the dismissible "not copied" notice on a seeded copy.
  const [copyNoticeDismissed, setCopyNoticeDismissed] = useState(false);
  // Recurring bookings (Phase 2) — weekly recurrence editor (create only). When on, occurrences are
  // generated UNASSIGNED, so crew + allocations are cleared and the single-booking save is replaced
  // by a POST to /api/booking-series.
  const [repeat, setRepeat] = useState(false);
  const [repeatDays, setRepeatDays] = useState<Set<number>>(() => new Set());
  const [repeatInterval, setRepeatInterval] = useState('1');
  const [repeatEnd, setRepeatEnd] = useState<'never' | 'until' | 'count'>('never');
  const [repeatUntil, setRepeatUntil] = useState('');
  const [repeatCount, setRepeatCount] = useState('10');
  // Phase 4 — monthly/yearly patterns + optional default pilot.
  const [repeatFreq, setRepeatFreq] = useState<'weekly' | 'monthly' | 'yearly'>('weekly');
  const [repeatMonthMode, setRepeatMonthMode] = useState<'by_date' | 'by_weekday'>('by_date');
  const [repeatMonthday, setRepeatMonthday] = useState('1');
  const [repeatSetpos, setRepeatSetpos] = useState('1'); // 1..5 or -1 (last)
  const [repeatWeekday, setRepeatWeekday] = useState('1'); // single weekday for monthly/yearly by_weekday
  const [repeatMonth, setRepeatMonth] = useState('1'); // yearly month 1-12
  const [defaultPilotId, setDefaultPilotId] = useState('');
  // §B double-booking hard-block prompt (Step 5c.3).
  const [collision, setCollision] = useState<CollisionDetails | null>(null);
  // PR-000010 soft challenge — set when an ordinary booking has no student/client nominated.
  // Holds the message AND the `extra` acknowledgement the interrupted
  // submit was carrying (e.g. { save_as_waitlist: true } from the collision panel), so "Proceed
  // anyway" replays BOTH acks. They answer two independent questions and can both be outstanding
  // at once, so they have to travel together — see the stash at the soft gate below.
  const [softConfirm, setSoftConfirm] = useState<{ message: string; extra: Record<string, unknown> } | null>(null);
  // 5d-p2b: offline create that overlaps CACHED data (advisory approximation, NOT §B) —
  // three-way prompt: queue anyway / queue as waitlist join / go back.
  const [offlinePrompt, setOfflinePrompt] = useState<{ payload: Record<string, unknown>; overlapLabels: string[] } | null>(null);
  const [displaceNotify, setDisplaceNotify] = useState<'instructor' | 'student' | 'both' | 'none'>('none');
  // Maintenance lifecycle fields (Step 5c.2.1).
  // ⚠ 0.12.7 Part D — seeded through maintConfirmation.ts, NOT from the stored note directly. The
  // note used to be stored as a rendered sentence and re-wrapped on every save (it nested without
  // bound on prod), and "Confirmed on" was seeded from the booking's date, so an unedited save moved
  // a correct confirmation date backwards. The seed is kept so an unedited save sends the stored
  // values back unchanged.
  const [maintSeed] = useState(() => seedMaintConfirmation(
    editing?.asset_allocations.find((a) => a.aircraft_registration) ?? null,
    editing?.dep_timezone ?? 'Australia/Darwin',
    init.date,
  ));
  const [maintWith, setMaintWith] = useState<string>(maintSeed.withText);
  const [maintDate, setMaintDate] = useState<string>(maintSeed.date);
  const [markUnserviceable, setMarkUnserviceable] = useState<boolean>(true);
  const upd = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const base = baseById[form.base_id];
  const selectedAircraft = aircraftRegs.map((r) => aircraft.find((a) => a.registration === r)).filter((a): a is Aircraft => !!a);
  const selectedAssets = assetIds.map((id) => assets.find((a) => a.id === id)).filter((a): a is Asset => !!a);
  const selectedKind = bookingKinds.find((k) => k.id === form.booking_kind_id);
  const isMaintenance = !!selectedKind?.is_maintenance;
  // Feature B — an "Unavailable" block is exempt from the instructor/student requirement (like
  // maintenance): it occupies a single resource (aircraft/asset/room) OR a staff participant.
  //
  // ⚠ is_unavailability IS DELIBERATELY NOT GIVEN THE ECD RULE BELOW. Ben said maintenance and
  // defects. An unavailability is a different thing — "block this aeroplane for 2 hours" is a
  // perfectly reasonable way to express one — so it keeps both entry modes until he says otherwise.
  const isUnavailability = !!selectedKind?.is_unavailability;

  // ⚠ A MAINTENANCE OR DEFECT BOOKING IS ALWAYS IN ECD MODE. Both modes write the SAME stored
  // fields (activity_mins + arr_time_planned, computed at payload time), so this is an entry
  // affordance only: no migration, no schema change, no backend validation change, and nothing
  // breaks for rows created the old way.
  //
  // ⚠ SAME-DAY STAYS VALID. The ordinary save builds the finish from finishDate + finishTime, so
  // an ECD equal to the start date with a later time — the uncommon one hour defect — is still
  // expressible and is not rejected anywhere.
  //
  // ⚠ AND AN EXISTING MAINTENANCE BOOKING WITH NO arr_time_planned DOES NOT OPEN BLANK: the edit
  // seed already derives finishTime from start + the booking's real length, so forcing ECD mode
  // shows that derived value rather than an empty field.
  useEffect(() => {
    if (isMaintenance) {
      if (entryMode !== 'finish') { maintModeStash.current = entryMode; setEntryMode('finish'); }
    } else if (maintModeStash.current) {
      setEntryMode(maintModeStash.current);
      maintModeStash.current = null;
    }
    // Keyed on the KIND only. entryMode is read but must not re-trigger this: while a maintenance
    // kind is selected the mode control is not offered, so there is nothing to fight with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaintenance]);

  const baseAircraft = useMemo(() => {
    const list = aircraft.filter((a) => !base?.icao || a.current_base_icao === base.icao);
    // Keep any already-selected aircraft visible even if not based here (e.g. when editing).
    const extra = aircraftRegs
      .filter((r) => !list.some((a) => a.registration === r))
      .map((r) => aircraft.find((a) => a.registration === r))
      .filter((a): a is Aircraft => !!a);
    return extra.length ? [...extra, ...list] : list;
  }, [aircraft, base?.icao, aircraftRegs]);
  const baseAssets = assets.filter((a) => a.base_id === form.base_id);
  const addableAircraft = baseAircraft.filter((a) => !aircraftRegs.includes(a.registration));
  const addableAssets = baseAssets.filter((a) => !assetIds.includes(a.id));

  const acLabel = (a: Aircraft): string => {
    const cap = a.capability_current && a.capability_current !== a.capability_normal ? ` · ⚠ ${a.capability_current}` : '';
    const home = a.base_icao !== a.current_base_icao ? ` (home: ${a.base_icao})` : '';
    // ⚠ DATE-AWARE, NOT `a.serviceable === false` (Ben, 2026-09-17). Booking a FUTURE maintenance
    // flips `serviceable` false AT CREATION, by design (bookings.service.ts markUnserviceable), so
    // the bare flag labelled the aircraft U/S on every date from today until the maintenance ends —
    // including dates weeks before it starts, where the aircraft is perfectly serviceable. The
    // board already guards this with aircraftStatus (panels.tsx), which suppresses U/S until
    // unserviceable_from has been reached ON THE VIEWED DAY; the form now reads the SAME function
    // against the booking's own date. Dates AFTER the ECD stay U/S while the aircraft is still
    // flagged unserviceable (awaiting return confirmation) — aircraftStatus only guards the
    // not-yet-commenced side, which is the correct asymmetry: an overdue return is real.
    const usDate = form.date || todayInTz(base?.timezone ?? 'Australia/Darwin');
    const us = aircraftStatus(a, usDate).kind === 'us' ? ' · U/S' : '';
    return `${a.registration} — ${a.model ?? a.aircraft_category}${cap}${us}${home}`;
  };

  // Participant editing — internal people are keyed by user_id (one per person); external
  // people can repeat, so rows are addressed by array index for rank/remove.
  const usedIds = useMemo(
    () => new Set(participants.map((p) => p.user_id).filter((id): id is string => id != null)),
    [participants],
  );
  // Combobox options (Phase 5). Instructors = staff (searched by full/preferred name); the
  // student/client picker = the bookable student+client list (searched by first/last/company).
  const instructorOptions: PersonOption[] = useMemo(
    () => staff.filter((s) => !usedIds.has(s.id)).map((s) => ({ id: s.id, label: personLabel(s.id), full_name: `${s.pilot_code ?? ''} ${s.full_name} ${s.preferred_name ?? ''}`.trim() })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staff, usedIds, people, extraLabels],
  );
  const studentOptions: PersonOption[] = useMemo(
    () => students.filter((s) => !usedIds.has(s.id)).map((s) => ({
      id: s.id, label: people.get(s.id)?.label ?? s.full_name,
      first_name: s.first_name, last_name: s.last_name, company: s.company, full_name: s.full_name,
    })),
    [students, usedIds, people],
  );
  // Rows partitioned by capacity, each carrying its original index for in-place edits.
  const indexed = participants.map((p, i) => ({ p, i }));
  const sortByRank = (a: { p: PartRow }, b: { p: PartRow }) => {
    if (a.p.rank != null && b.p.rank != null) return a.p.rank - b.p.rank;
    if (a.p.rank != null) return -1;
    if (b.p.rank != null) return 1;
    return CAPACITY_ORDER.indexOf(a.p.capacity) - CAPACITY_ORDER.indexOf(b.p.capacity);
  };
  // Instructor side = everything NOT on the student line (instructor + externals); student side =
  // student + client (both occupy the non-instructor person line).
  const instructorRows = indexed.filter(({ p }) => !STUDENT_LINE_CAPACITIES.includes(p.capacity)).sort(sortByRank);
  const studentRows = indexed.filter(({ p }) => STUDENT_LINE_CAPACITIES.includes(p.capacity)).sort(sortByRank);

  function addInstructor(value: string) {
    if (!value) return;
    const ext = EXTERNAL_OPTIONS.find((o) => o.value === value);
    if (ext) {
      const name = window.prompt(`${ext.value === 'external_examiner' ? 'External examiner' : 'External instructor'} name:`)?.trim();
      if (!name) return;
      setParticipants((ps) => [...ps, { user_id: null, capacity: ext.value, external_name: name, rank: null }]);
      return;
    }
    setParticipants((ps) => [...ps, { user_id: value, capacity: 'instructor', external_name: null, rank: null }]);
  }
  function addStudent(id: string) {
    if (!id) return;
    // Default the capacity from the person's kind (student→student, client→client). The operator
    // can override per booking; the picker control for that lands in Phase 5.
    const capacity: ParticipantCapacity = people.get(id)?.kind === 'client' ? 'client' : 'student';
    setParticipants((ps) => [...ps, { user_id: id, capacity, external_name: null, rank: null }]);
  }
  const setRankAt = (i: number, rank: number | null) => setParticipants((ps) => ps.map((p, idx) => (idx === i ? { ...p, rank } : p)));
  const removeAt = (i: number) => setParticipants((ps) => ps.filter((_, idx) => idx !== i));

  // Inline "Create new" (Phase 5). Split the typed text into first/last for the student/client
  // modal prefill. Instructor create opens the full staff form (admin-only) with no prefill.
  const splitName = (typed: string): { first_name?: string; last_name?: string } => {
    const [first, ...rest] = typed.trim().split(/\s+/);
    return { first_name: first || undefined, last_name: rest.join(' ') || undefined };
  };
  // After an inline create, stash the label and add the new person to the booking immediately
  // (they're added by id + capacity; they also appear in the picker on the next reference refresh).
  function addCreatedParticipant(id: string, label: string, capacity: ParticipantCapacity) {
    setExtraLabels((m) => new Map(m).set(id, label));
    setParticipants((ps) => [...ps, { user_id: id, capacity, external_name: null, rank: null }]);
    setCreateFlow(null);
    // PR-000008 — refresh the people reference so the new person is in the picker (not just this booking).
    props.onPeopleChanged?.();
  }
  const baseOptions = useMemo(() => bases.map((b) => ({ value: b.id, label: `${b.name} (${b.short_code})` })), [bases]);

  // Advisory hints — INTERNAL instructor participants whose staff record needs approval.
  const crewNeedingApproval = participants
    .filter((p) => p.capacity === 'instructor' && p.user_id != null)
    .map((p) => staff.find((s) => s.id === p.user_id))
    .filter((s): s is Staff => !!s && (s.availability_mode !== 'always' || s.approval_required));
  const acsNeedingApproval = selectedAircraft.filter((a) => a.approval_required);
  const assetsNeedingConfirm = selectedAssets.filter(
    (a) => a.ownership !== 'school_owned' || a.ppr_required || a.require_booking_confirmation,
  );

  function buildPayload(extra: Record<string, unknown>): Record<string, unknown> {
    const tz = base?.timezone ?? 'Australia/Darwin';
    // Whole-day: 00:00 on the start date → 00:00 on the day AFTER the chosen last day (true 1440 min
    // per day; multi-day when lastDay > start date). Derived here so the user never sees the +1-day
    // midnight boundary. Otherwise: normal start-time + (duration | finish date/time).
    const startIso = wholeDay ? localToUtcIso(form.date, '00:00', tz) : localToUtcIso(form.date, form.startTime, tz);
    let endIso: string;
    if (wholeDay) endIso = localToUtcIso(addDays(lastDay, 1), '00:00', tz);
    else if (entryMode === 'finish') endIso = localToUtcIso(form.finishDate, form.finishTime, tz);
    else endIso = new Date(new Date(startIso).getTime() + form.durationMins * 60_000).toISOString();
    const activityMins = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000);

    interface AllocInput {
      aircraft_registration?: string; asset_id?: string;
      external_aircraft_source?: ExternalAircraftSource; external_aircraft_type?: string;
      external_aircraft_registration?: string; external_aircraft_invoicer?: string;
      booking_confirmation_note?: string; booking_confirmed_via?: string; booking_confirmed_at?: string;
    }
    const allocations: AllocInput[] = [];
    for (const reg of aircraftRegs) {
      const a: AllocInput = { aircraft_registration: reg };
      // 0.12.7 Part D: stores what was TYPED; the sentence is rendered at read time.
      const mc = isMaintenance ? buildMaintConfirmation(maintWith, maintDate, tz, maintSeed) : null;
      if (mc) {
        a.booking_confirmation_note = mc.booking_confirmation_note;
        a.booking_confirmed_via = mc.booking_confirmed_via;
        a.booking_confirmed_at = mc.booking_confirmed_at;
      }
      allocations.push(a);
    }
    for (const id of assetIds) allocations.push({ asset_id: id });
    for (const e of externalAc) {
      const a: AllocInput = {
        external_aircraft_source: e.source,
        external_aircraft_type: e.type.trim(),
        external_aircraft_registration: e.registration.trim(),
      };
      // Invoicer is Crosshire-only (server rejects it otherwise); omit empties.
      if (e.source === 'crosshire' && e.invoicer.trim()) a.external_aircraft_invoicer = e.invoicer.trim();
      allocations.push(a);
    }

    const payload: Record<string, unknown> = {
      base_id: form.base_id,
      block_start_at: startIso,
      arr_time_planned: endIso,
      activity_mins: activityMins,
      allocations,
      participants: participants.map((p) => ({
        capacity: p.capacity,
        ...(p.user_id != null ? { user_id: p.user_id } : {}),
        ...(p.external_name != null ? { external_name: p.external_name } : {}),
        ...(rankAllowed && p.rank != null ? { rank: p.rank } : {}),
      })),
      max_participants: maxParticipants.trim() ? Number(maxParticipants) : null,
      participants_limit_hard: hardLimit,
      ...extra,
    };
    if (isMaintenance && aircraftRegs.length) payload.maintenance_mark_unserviceable = markUnserviceable;
    if (form.activity_id) payload.activity_id = form.activity_id;
    else if (editing) payload.activity_id = null;
    if (form.booking_kind_id) payload.booking_kind_id = form.booking_kind_id;
    else if (editing) payload.booking_kind_id = null;
    // Feature D — optional title; null clears on edit.
    payload.title = form.title.trim() || (editing ? null : undefined);
    payload.notes = form.notes || (editing ? null : undefined);
    // Staff-only note — same present-versus-absent discipline as notes (null clears on edit,
    // omitted on create). Written to its OWN column; it never touches the shared note.
    payload.internal_note = form.internal_note || (editing ? null : undefined);
    payload.reminder_enabled = remindInstructor;
    return payload;
  }

  async function submit(extra: Record<string, unknown> = {}, ackSoft = false) {
    setError(null);
    setSoftConfirm(null);
    // Recurring (Phase 2): create a series instead of a single booking. Occurrences are generated
    // unassigned, so this path skips the crew/allocation requirements below.
    if (repeat) { await submitSeries(); return; }

    // ── The decision chain (extracted 2026-08-30) ──────────────────────────────────────────────
    // Every client-side validity rule, IN ORDER, plus the no-student soft challenge, now lives in
    // the pure evaluateBookingSubmit(). Order is load-bearing and is asserted by
    // database/verify-booking-submit-gate.ts. Behaviour here is unchanged: each `error` is the
    // setError() that used to sit inline, `softConfirm` is the stash that fixes item D, and
    // `proceed` runs buildPayload(extra) exactly as before.
    //
    // Built as an explicit literal, NOT a cast: every key is checked against SubmitInput by the
    // compiler, so a renamed piece of form state is a build error rather than a silent undefined.
    const decisionInput: SubmitInput = {
      base_id: form.base_id,
      booking_kind_id: form.booking_kind_id,
      date: form.date,
      startTime: form.startTime,
      entryMode,
      finishDate: form.finishDate,
      finishTime: form.finishTime,
      wholeDay,
      lastDay,
      aircraftRegs,
      assetIds,
      externalAc,
      isMaintenance,
      isUnavailability,
      editingSeriesId: editing?.series_id ?? null,
      participants,
      timezone: base?.timezone,
    };
    const decision = evaluateBookingSubmit(decisionInput, extra, ackSoft);
    if (decision.kind === 'error') { setError(decision.message); return; }
    if (decision.kind === 'softConfirm') {
      setSoftConfirm({ message: decision.message, extra: decision.extra });
      return;
    }

    const payload = buildPayload(decision.extra);
    setSubmitting(true);
    try {
      const res = editing
        ? await apiFetch<Booking>(`/api/bookings/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await apiFetch<Booking>('/api/bookings', { method: 'POST', body: JSON.stringify(payload) });
      if (res.error) {
        const code = res.error.code;
        if (code === 'BOOKING_COLLISION' || code === 'PENDING_DISPLACEMENT' || code === 'MAINTENANCE_DISPLACEMENT') {
          setCollision((res.error.details as CollisionDetails) ?? null);
        } else {
          setError(res.error.message);
        }
        return;
      }
      props.onSubmitted(res.warnings, res.data ?? null);
    } catch (e) {
      // 5d-p2: a NETWORK rejection (offline — never an HTTP error response) queues the op
      // for replay instead of failing. The optimistic provisional overlay renders it on
      // the board until the drain acks it.
      if (e instanceof NetworkError && user) {
        const readStamp = getBookingsReadStamp();
        if (editing?._pending_op?.kind === 'create') {
          // Editing a booking that is itself still local_pending → amend the queued
          // CREATE in place (no op chains in Phase 2). Strip the edit-mode explicit
          // nulls — a create payload omits cleared optional fields rather than nulling.
          const amended = { ...payload };
          for (const k of ['activity_id', 'booking_kind_id', 'notes', 'internal_note'] as const) {
            if (amended[k] === null) delete amended[k];
          }
          const ok = await amendQueuedOp(editing._pending_op.queueKey, amended, readStamp);
          if (ok) { props.onSubmitted(['Offline — queued booking updated. It will sync when reconnected.'], null); return; }
        } else if (editing) {
          // 5d-p5: per-action staleness advisory (Class A) — record the firing on the op and
          // surface §8 wording inline; non-blocking (the op queues regardless).
          const stale = stalenessFor('booking_edit', readStamp);
          const op = await enqueueOp(user.id, 'booking_edit', { ...payload, task_id: editing.id }, readStamp, stale.record);
          if (op) {
            const msgs = ['Offline — edit saved to this device. It will sync automatically when reconnected; availability can\'t be confirmed until then.'];
            if (stale.message) msgs.unshift(stale.message);
            props.onSubmitted(msgs, null);
            return;
          }
        } else {
          // 5d-p2b: NEW booking offline — lightweight overlap advisory against the
          // CACHED scope (same aircraft, interval overlap; an approximation, clearly
          // NOT §B). Overlap → three-way prompt; clear → queue as an ordinary create.
          const overlaps = await findCachedOverlaps(payload);
          if (overlaps.length > 0) { setOfflinePrompt({ payload, overlapLabels: overlaps }); return; }
          if (await queueOfflineCreate(payload, false)) return;
        }
        setError('Offline, and the on-device queue is unavailable — this change could not be saved.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setSubmitting(false);
    }
  }

  // 5d-p2b: cached-data overlap advisory for an offline create — same-aircraft interval
  // overlap against last-known (cached) bookings. Returns the overlapping aircraft regs.
  async function findCachedOverlaps(payload: Record<string, unknown>): Promise<string[]> {
    const allocs = (payload.allocations as Array<{ aircraft_registration?: string }> | undefined) ?? [];
    const regs = new Set(allocs.map((a) => a.aircraft_registration).filter((r): r is string => !!r));
    if (regs.size === 0) return [];
    const start = new Date(payload.block_start_at as string).getTime();
    const end = new Date(payload.arr_time_planned as string).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
    const cached = await readAllCachedBookings();
    const hits = new Set<string>();
    for (const b of cached) {
      if (b.booking_status === 'cancelled' || b.waitlisted || !b.block_start_at) continue;
      const bs = new Date(b.block_start_at).getTime();
      const be = b.arr_time_planned ? new Date(b.arr_time_planned).getTime() : bs + (b.activity_mins ?? 60) * 60_000;
      if (!(bs < end && be > start)) continue;
      for (const a of b.asset_allocations) {
        if (a.aircraft_registration && regs.has(a.aircraft_registration)) hits.add(a.aircraft_registration);
      }
    }
    return [...hits];
  }

  // Queue an offline create — as an ordinary booking_create, or as a waitlist_join
  // carrying the user's save_as_waitlist ack (recorded NOW, honoured at sync — v2 §9).
  async function queueOfflineCreate(payload: Record<string, unknown>, asJoin: boolean): Promise<boolean> {
    if (!user) return false;
    const readStamp = getBookingsReadStamp();
    const opType = asJoin ? 'waitlist_join' : 'booking_create';
    // 5d-p5: Class A advisory (create + waitlist_join), recorded on the op + surfaced inline.
    const stale = stalenessFor(opType, readStamp);
    const op = asJoin
      ? await enqueueOp(user.id, 'waitlist_join', { ...payload, save_as_waitlist: true }, readStamp, stale.record)
      : await enqueueOp(user.id, 'booking_create', payload, readStamp, stale.record);
    if (!op) return false;
    setOfflinePrompt(null);
    const msgs = [asJoin
      ? 'Offline — waitlist join saved to this device. At sync it joins the waitlist if the slot is still taken, or books directly if it has freed.'
      : 'Offline — booking saved to this device. It will sync automatically when reconnected; availability can\'t be confirmed until then.'];
    if (stale.message) msgs.unshift(stale.message);
    props.onSubmitted(msgs, null);
    return true;
  }

  // ── Recurring bookings (Phase 2) ───────────────────────────────────────────────
  const repeatIntervalNum = Math.max(1, parseInt(repeatInterval, 10) || 1);
  const repeatDaysLabel = WEEKDAY_ORDER.filter((d) => repeatDays.has(d)).map((d) => DAY_LABELS[d]).join(', ');
  const repeatEndLabel =
    repeatEnd === 'never' ? 'until cancelled'
    : repeatEnd === 'until' ? (repeatUntil ? `until ${repeatUntil}` : 'until a date')
    : `for ${Math.max(1, parseInt(repeatCount, 10) || 1)} occurrences`;
  const pilotStaff = staff.find((s) => s.id === defaultPilotId);
  const pilotLabel = defaultPilotId ? (pilotStaff ? staffLabel(pilotStaff) : 'a pilot') : null;
  const assignLabel = pilotLabel ? `assigned to ${pilotLabel}` : 'unassigned';
  // Plain-English pattern phrase for the live summary.
  const repeatPattern = (() => {
    const every = repeatIntervalNum > 1 ? `${repeatIntervalNum} ` : '';
    if (repeatFreq === 'weekly') return repeatDays.size === 0 ? null : `Every ${every}week${repeatIntervalNum > 1 ? 's' : ''} on ${repeatDaysLabel}`;
    if (repeatFreq === 'monthly') {
      return repeatMonthMode === 'by_date'
        ? `Every ${every}month${repeatIntervalNum > 1 ? 's' : ''} on day ${repeatMonthday}`
        : `Every ${every}month${repeatIntervalNum > 1 ? 's' : ''} on the ${SETPOS_LABELS[repeatSetpos]} ${DAY_LABELS[Number(repeatWeekday)]}`;
    }
    const ydetail = repeatMonthMode === 'by_date' ? `${repeatMonthday} ${MONTH_LABELS[Number(repeatMonth)]}` : `the ${SETPOS_LABELS[repeatSetpos]} ${DAY_LABELS[Number(repeatWeekday)]} of ${MONTH_LABELS[Number(repeatMonth)]}`;
    return `Every ${every}year${repeatIntervalNum > 1 ? 's' : ''} on ${ydetail}`;
  })();
  const repeatSummary = repeatPattern ? `${repeatPattern} — ${assignLabel} — ${repeatEndLabel}` : 'Pick at least one weekday.';

  function onToggleRepeat(on: boolean) {
    setRepeat(on);
    if (!on) return;
    // Pre-select the start date's weekday for convenience.
    if (repeatDays.size === 0 && form.date) {
      const [y, mo, d] = form.date.split('-').map(Number);
      setRepeatDays(new Set([new Date(Date.UTC(y, mo - 1, d)).getUTCDay()]));
    }
    // Occurrences are created unassigned — clear any crew/aircraft already chosen (notice shown).
    if (participants.length || aircraftRegs.length || assetIds.length || externalAc.length) {
      setParticipants([]); setAircraftRegs([]); setAssetIds([]); setExternalAc([]);
    }
  }

  // Whole-day toggle. On tick: stash the current time entry, force finish-mode midnight-to-midnight,
  // and default the visible "Last day" to the start date. On untick: restore the stashed entry.
  // (Mutually exclusive with Repeat for v1 — a 00:00→00:00 occurrence trips the series' same-day
  // finish>start guard; both checkboxes disable the other while on.)
  function onToggleWholeDay(on: boolean) {
    setWholeDay(on);
    if (on) {
      wholeDayStash.current = { entryMode, startTime: form.startTime, finishTime: form.finishTime, finishDate: form.finishDate, durationMins: form.durationMins };
      setLastDay(form.date || todayInTz(base?.timezone ?? 'Australia/Darwin'));
      setEntryMode('finish');
      setForm((p) => ({ ...p, startTime: '00:00', finishTime: '00:00' }));
      setError(null);
    } else {
      const s = wholeDayStash.current;
      if (s) {
        // ⚠ A maintenance kind may have been selected WHILE whole-day was ticked, in which case
        // the stashed mode is the pre-maintenance one and restoring it verbatim would drop an ECD
        // booking back into duration mode behind the user's back.
        setEntryMode(isMaintenance ? 'finish' : s.entryMode);
        setForm((p) => ({ ...p, startTime: s.startTime, finishTime: s.finishTime, finishDate: s.finishDate, durationMins: s.durationMins }));
        wholeDayStash.current = null;
      }
    }
  }

  // Build the series template (the form-modeled subset) + POST to /api/booking-series. Replaces the
  // single-booking save when Repeat is on. The server re-runs nothing per-occurrence (they're
  // unassigned); §B/PPR/approval run later when each day is allocated via the normal PUT.
  async function submitSeries() {
    if (!form.base_id) { setError('Base is required.'); return; }
    // ⚠ THE SERIES PATH NEEDS ITS OWN COPY of the booking-kind rule. submitSeries() returns out of
    // submit() BEFORE evaluateBookingSubmit() is reached (see the `if (repeat)` branch above), so
    // the rule 3 added to the decision chain does not run here. Same wording, so an operator who
    // ticks Repeat gets the same sentence they would have got without it.
    if (!form.booking_kind_id) { setError('Booking type is required.'); return; }
    if (!form.date) { setError('Pick a start date for the recurring booking.'); return; }
    if (repeatFreq === 'weekly' && repeatDays.size === 0) { setError('Pick at least one weekday to repeat on.'); return; }
    if (repeatEnd === 'until' && !repeatUntil) { setError('Pick an end date for the repeat.'); return; }

    const tz = base?.timezone ?? 'Australia/Darwin';
    // Series occurrences are same-day (template stores local_time + activity_mins), so there is no
    // overnight case — a finish at/before the start is invalid. Validate BEFORE building the payload.
    if (entryMode === 'finish' && minutesOf(form.finishTime) <= minutesOf(form.startTime)) {
      setError('Finish time must be after the start time.'); return;
    }
    const activityMins = entryMode === 'duration'
      ? form.durationMins
      : minutesOf(form.finishTime) - minutesOf(form.startTime);
    if (activityMins <= 0) { setError('Duration must be greater than zero.'); return; }

    const template: Record<string, unknown> = { base_id: form.base_id, activity_mins: activityMins, participants_limit_hard: hardLimit };
    if (form.activity_id) template.activity_id = form.activity_id;
    // Always set — the guard above has already rejected an empty kind.
    template.booking_kind_id = form.booking_kind_id;
    if (form.title.trim()) template.title = form.title.trim();
    if (maxParticipants.trim()) template.max_participants = Number(maxParticipants);
    if (form.notes.trim()) template.notes = form.notes;
    // Staff-only note rides the series template (a Json column, so no migration). The reminder
    // arm flag deliberately does NOT: occurrences are generated unassigned, so an armed reminder
    // would have no instructor to reach. Arm it per occurrence once someone is allocated.
    if (form.internal_note.trim()) template.internal_note = form.internal_note;

    const payload: Record<string, unknown> = {
      freq: repeatFreq,
      interval: repeatIntervalNum,
      dtstart_date: form.date,
      local_time: form.startTime,
      timezone: tz,
      end_mode: repeatEnd,
      template,
    };
    if (repeatFreq === 'weekly') {
      payload.by_weekday = WEEKDAY_ORDER.filter((d) => repeatDays.has(d));
    } else if (repeatFreq === 'monthly') {
      payload.month_mode = repeatMonthMode;
      if (repeatMonthMode === 'by_date') payload.by_monthday = Number(repeatMonthday);
      else { payload.by_setpos = Number(repeatSetpos); payload.by_weekday = [Number(repeatWeekday)]; }
    } else {
      payload.by_month = Number(repeatMonth);
      if (repeatMonthMode === 'by_date') payload.by_monthday = Number(repeatMonthday);
      else { payload.by_setpos = Number(repeatSetpos); payload.by_weekday = [Number(repeatWeekday)]; }
    }
    if (defaultPilotId) payload.default_assignee_user_id = defaultPilotId;
    if (repeatEnd === 'until') payload.until_date = repeatUntil;
    if (repeatEnd === 'count') payload.count = Math.max(1, parseInt(repeatCount, 10) || 1);

    setSubmitting(true);
    try {
      const res = await apiFetch<{ occurrenceCount: number }>('/api/booking-series', { method: 'POST', body: JSON.stringify(payload) });
      if (res.error) { setError(res.error.message); return; }
      const n = res.data?.occurrenceCount ?? 0;
      props.onSubmitted([`Created ${n} booking${n === 1 ? '' : 's'} (${repeatSummary}).`], null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setSubmitting(false);
    }
  }

  const hint = (text: string, color: string, bg: string, border: string) => (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: '8px 12px', marginBottom: 8, fontSize: 11, color }}>
      {text}
    </div>
  );

  return (
    <>
    <ModalOverlay onClose={props.onClose}>
      <div
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24,
          width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)', fontFamily: F,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{editing ? 'Edit Booking' : seed ? 'Copy Booking' : 'New Booking'}</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>Flight school scheduling</div>
          </div>
          <button onClick={props.onClose} style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        {/* Copy context (recurring/copy Phase 1) — the setup was carried; the operator must choose a
            new date/time and re-add crew. The setup the create path can't reproduce is listed
            plainly (re-set via Edit after save) so it's never silently lost. Dismissible. */}
        {seed && !copyNoticeDismissed && (
          <div style={{ position: 'relative', background: '#eff6ff', border: `1px solid ${C.accentBorder}`, borderRadius: 6, padding: '8px 28px 8px 12px', marginBottom: 12, fontSize: 11, color: C.accent, lineHeight: 1.5 }}>
            <button type="button" onClick={() => setCopyNoticeDismissed(true)} title="Dismiss"
              style={{ position: 'absolute', top: 4, right: 6, background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>✕</button>
            <strong>Copied the setup.</strong> Choose a new date/time and re-add crew below.
            {seed.notCopied.length > 0 && (
              <div style={{ marginTop: 4, color: C.amber }}>
                These weren't copied and may need re-entering: {seed.notCopied.join(', ')}.
              </div>
            )}
          </div>
        )}

        <Field label="Booking Location" req>
          <select value={form.base_id} onChange={(e) => upd('base_id', e.target.value)} style={inputStyle}>
            {!form.base_id && <option value="">— select base —</option>}
            {bases.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.short_code})</option>)}
          </select>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 12px' }}>
          <Field label="Date" req>
            <input type="date" value={form.date}
              onChange={(e) => { const v = e.target.value; upd('date', v); if (wholeDay && (!lastDay || lastDay < v)) setLastDay(v); }}
              style={inputStyle} />
          </Field>
          <Field label="Start Time" req>
            {wholeDay ? (
              <div style={{ ...inputStyle, color: C.textMuted, background: C.pill, cursor: 'not-allowed' }} title="Whole-day booking runs from midnight to midnight">All day (00:00 → 00:00)</div>
            ) : (
              <input type="time" value={form.startTime} onChange={(e) => upd('startTime', e.target.value)} style={inputStyle} />
            )}
            {/* Whole-day (email 2026-07-11) — 00:00 → next-day 00:00, multi-day via "Last day".
                Mutually exclusive with Repeat for v1 (a 00:00→00:00 series occurrence trips the
                same-day finish>start guard). */}
            <label title={repeat ? 'Not yet supported for repeating bookings' : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginTop: 5, color: repeat ? C.textMuted : C.textDim, cursor: repeat ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={wholeDay} disabled={repeat} onChange={(e) => onToggleWholeDay(e.target.checked)} style={{ accentColor: C.accent }} />
              Whole day
            </label>
          </Field>
          <div style={{ marginBottom: 10 }}>
            {wholeDay ? (
              <>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.textDim, marginBottom: 2, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Last day</div>
                <input type="date" value={lastDay} min={form.date || undefined} onChange={(e) => setLastDay(e.target.value)} title="Last whole day of the booking (same as the start date for a single day; later for multi-day)" style={inputStyle} />
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 4, marginBottom: 3 }}>
                  {/* ⚠ ONE control on a maintenance kind: the Duration option is not offered at
                      all, rather than offered and ignored. */}
                  {(isMaintenance ? (['finish'] as const) : (['duration', 'finish'] as const)).map((m) => (
                    <button key={m} type="button" onClick={() => setEntryMode(m)}
                      style={{
                        flex: 1, padding: '3px 4px', fontSize: 9, fontWeight: 700, fontFamily: F, cursor: 'pointer',
                        textTransform: 'uppercase', letterSpacing: '0.04em', borderRadius: 5,
                        border: `1px solid ${entryMode === m ? C.navy : C.border}`,
                        background: entryMode === m ? C.navy : C.surface, color: entryMode === m ? '#fff' : C.textDim,
                      }}>
                      {m === 'duration' ? 'Duration' : isMaintenance ? 'ECD' : 'Finish'}
                    </button>
                  ))}
                </div>
                {entryMode === 'duration' ? (
                  <select value={form.durationMins} onChange={(e) => setForm((p) => ({ ...p, durationMins: Number(e.target.value) }))} style={inputStyle}>
                    {/* PR-000057 — an off-grid length (a 300 min Finish-mode booking, a 47 min one) is
                        INJECTED as an extra option for this form instance, never substituted. */}
                    {durationOptions(form.durationMins).map((m) => (
                      <option key={m} value={m}>{formatDurationLabel(m)}</option>
                    ))}
                  </select>
                ) : (
                  <>
                    {isMaintenance && <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, marginBottom: 2, letterSpacing: '0.02em' }}>Expected completion (ECD)</div>}
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input type="date" value={form.finishDate} onChange={(e) => upd('finishDate', e.target.value)} title={isMaintenance ? 'Expected completion date (ECD)' : 'Finish date (multi-day)'} style={{ ...inputStyle, flex: 1.4, padding: '7px 6px' }} />
                      <input type="time" value={form.finishTime} onChange={(e) => upd('finishTime', e.target.value)} title={isMaintenance ? 'Expected completion time' : undefined} style={{ ...inputStyle, flex: 1, padding: '7px 6px' }} />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <Field label="Title (optional)">
          <input value={form.title} onChange={(e) => upd('title', e.target.value)} maxLength={80} placeholder="Optional title for this booking" style={inputStyle} />
          {/* Ben, 2026-09-07: say what the reader will actually SEE. The old sentence ended "and
              the task", which named an internal row rather than anything on screen. This one
              tracks the two chip rows the title would otherwise replace: row two is the person
              (bookingLabel.personName) and row three is taskRowLabel, which composes
              "Kind - Activity". Keep the two in step — if taskRowLabel's composition changes,
              this sentence is wrong and there is nothing but this comment to catch it. */}
          <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 3, lineHeight: 1.4 }}>
            Leave blank and the booking shows the student or client name, then the booking type and activity.
          </div>
        </Field>

        {/* ── Recurring (create/copy only) — weekly/monthly/yearly + optional default pilot ─────── */}
        {!editing && (
          <>
            <label title={wholeDay ? 'Not available while “Whole day” is ticked' : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: wholeDay ? C.textMuted : C.textDim, cursor: wholeDay ? 'not-allowed' : 'pointer', marginBottom: repeat ? 8 : 10 }}>
              <input type="checkbox" checked={repeat} disabled={wholeDay} onChange={(e) => onToggleRepeat(e.target.checked)} style={{ accentColor: C.accent }} />
              🔁 Repeat (recurring booking)
            </label>
            {repeat && (
              <div style={{ background: '#f5f6ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                {/* Frequency */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                  {(['weekly', 'monthly', 'yearly'] as const).map((f) => (
                    <button key={f} type="button" onClick={() => setRepeatFreq(f)}
                      style={{ flex: 1, padding: '5px 2px', fontSize: 10, fontWeight: 700, fontFamily: F, textTransform: 'capitalize', cursor: 'pointer', borderRadius: 5, border: `1px solid ${repeatFreq === f ? C.navy : C.border}`, background: repeatFreq === f ? C.navy : C.surface, color: repeatFreq === f ? '#fff' : C.textDim }}>
                      {f}
                    </button>
                  ))}
                </div>

                {repeatFreq === 'weekly' && (
                  <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
                    {WEEKDAY_ORDER.map((d) => {
                      const on = repeatDays.has(d);
                      return (
                        <button key={d} type="button"
                          onClick={() => setRepeatDays((s) => { const n = new Set(s); if (n.has(d)) n.delete(d); else n.add(d); return n; })}
                          style={{ flex: 1, minWidth: 34, padding: '5px 2px', fontSize: 10, fontWeight: 700, fontFamily: F, cursor: 'pointer', borderRadius: 5, border: `1px solid ${on ? C.navy : C.border}`, background: on ? C.navy : C.surface, color: on ? '#fff' : C.textDim }}>
                          {DAY_LABELS[d]}
                        </button>
                      );
                    })}
                  </div>
                )}

                {(repeatFreq === 'monthly' || repeatFreq === 'yearly') && (
                  <>
                    {repeatFreq === 'yearly' && (
                      <Field label="Month">
                        <select value={repeatMonth} onChange={(e) => setRepeatMonth(e.target.value)} style={inputStyle}>
                          {MONTH_LABELS.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                      </Field>
                    )}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      {(['by_date', 'by_weekday'] as const).map((m) => (
                        <button key={m} type="button" onClick={() => setRepeatMonthMode(m)}
                          style={{ flex: 1, padding: '5px 2px', fontSize: 10, fontWeight: 700, fontFamily: F, cursor: 'pointer', borderRadius: 5, border: `1px solid ${repeatMonthMode === m ? C.navy : C.border}`, background: repeatMonthMode === m ? C.navy : C.surface, color: repeatMonthMode === m ? '#fff' : C.textDim }}>
                          {m === 'by_date' ? 'On a date' : 'On a weekday'}
                        </button>
                      ))}
                    </div>
                    {repeatMonthMode === 'by_date' ? (
                      <Field label="Day of month (29–31 clamp to month-end)">
                        <input type="number" min={1} max={31} value={repeatMonthday} onChange={(e) => setRepeatMonthday(e.target.value)} style={inputStyle} />
                      </Field>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 12px' }}>
                        <Field label="Position">
                          <select value={repeatSetpos} onChange={(e) => setRepeatSetpos(e.target.value)} style={inputStyle}>
                            <option value="1">First</option><option value="2">Second</option><option value="3">Third</option>
                            <option value="4">Fourth</option><option value="5">Fifth</option><option value="-1">Last</option>
                          </select>
                        </Field>
                        <Field label="Weekday">
                          <select value={repeatWeekday} onChange={(e) => setRepeatWeekday(e.target.value)} style={inputStyle}>
                            {WEEKDAY_ORDER.map((d) => <option key={d} value={d}>{DAY_LABELS[d]}</option>)}
                          </select>
                        </Field>
                      </div>
                    )}
                  </>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 12px' }}>
                  <Field label={`Every (${repeatFreq === 'weekly' ? 'weeks' : repeatFreq === 'monthly' ? 'months' : 'years'})`}>
                    <input type="number" min={1} value={repeatInterval} onChange={(e) => setRepeatInterval(e.target.value)} style={inputStyle} />
                  </Field>
                  <Field label="Ends">
                    <select value={repeatEnd} onChange={(e) => setRepeatEnd(e.target.value as 'never' | 'until' | 'count')} style={inputStyle}>
                      <option value="never">Never</option>
                      <option value="until">On date</option>
                      <option value="count">After…</option>
                    </select>
                  </Field>
                </div>
                {repeatEnd === 'until' && (
                  <Field label="End date"><input type="date" value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} style={inputStyle} /></Field>
                )}
                {repeatEnd === 'count' && (
                  <Field label="Number of occurrences"><input type="number" min={1} value={repeatCount} onChange={(e) => setRepeatCount(e.target.value)} style={inputStyle} /></Field>
                )}

                {/* Optional default pilot — pre-fills every occurrence (overridable per day). */}
                <Field label="Default pilot (optional)">
                  <select value={defaultPilotId} onChange={(e) => setDefaultPilotId(e.target.value)} style={inputStyle}>
                    <option value="">— unassigned (allocate each day) —</option>
                    {staff.map((s) => <option key={s.id} value={s.id}>{staffLabel(s)}</option>)}
                  </select>
                </Field>

                <div style={{ fontSize: 11, color: C.accent, fontWeight: 600, marginTop: 6 }}>{repeatSummary}</div>
                <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 4, lineHeight: 1.4 }}>
                  {defaultPilotId
                    ? 'Each occurrence is pre-filled with the default pilot; any day that clashes is flagged (not blocked) for you to resolve.'
                    : 'Recurring bookings are created unassigned — you\'ll allocate crew and aircraft each day.'}
                </div>
              </div>
            )}
          </>
        )}

        <Field label={`Students / clients${rankAllowed ? ' (ranked)' : ''}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {studentRows.map(({ p, i }) => (
              <div key={`s-${i}`} style={chipRow}>
                {rankAllowed && (
                  <input
                    type="number" min={1} value={p.rank ?? ''} placeholder="#" title="Rank (optional; blank = co-equal)"
                    onChange={(e) => setRankAt(i, e.target.value ? Math.max(1, Number(e.target.value)) : null)}
                    style={{ ...inputStyle, width: 38, padding: '4px 4px', textAlign: 'center' }}
                  />
                )}
                <span style={chipText}>{partLabel(p)}</span>
                {p.capacity === 'client' && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>Client</span>
                )}
                <button type="button" onClick={() => removeAt(i)} title="Remove" style={chipX}>✕</button>
              </div>
            ))}
            <PersonCombobox
              placeholder="Search students / clients…"
              options={studentOptions}
              onSelect={addStudent}
              // "Create new" opens the trimmed student/client modal (all staff), prefilled from the typed text.
              onCreateNew={(typed) => setCreateFlow({ kind: 'person', prefill: splitName(typed) })}
            />
          </div>
        </Field>

        {/* ⚠ BOOKING KIND RENDERS ABOVE ACTIVITY AND IS REQUIRED (Ben, 2026-09-07). The kind is
            what the chip colours by and what drives the maintenance / unavailability behaviour, so
            it is the primary choice; the activity qualifies it and is optional.

            ⚠ MANDATORY IN THIS FORM, NOT IN THE DATABASE. `Task.booking_kind_id` stays nullable and
            live kindless bookings EXIST — one was hit on the board on 2026-09-07. Making the column
            NOT NULL would need a backfill and a ruling on what those rows become, which is out of
            scope. Existing rows are left alone; only a save THROUGH THIS FORM is gated. One
            consequence is deliberate: editing a pre-existing kindless booking now requires picking
            a kind before it will save. That is the rule working, not a regression.

            The blocking rule itself lives in evaluateBookingSubmit() (rule 3), not here, so it is
            covered by database/verify-booking-submit-gate.ts. */}
        <Field label="Booking Kind" req>
          <select
            value={form.booking_kind_id}
            onChange={(e) => {
              // ⚠ THE ECD FORCING USED TO LIVE HERE and has moved to the isMaintenance effect
              // above. It had to: this handler ran BEFORE the effect and set 'finish' directly,
              // so by the time the effect looked, the mode was already 'finish' and there was
              // nothing left to stash — switching the kind back then left the form stuck in
              // Finish mode instead of restoring Duration. It was also create-only (`!editing`)
              // and had no restore at all. One owner, and it covers mount, edit and restore.
              upd('booking_kind_id', e.target.value);
            }}
            style={inputStyle}
          >
            <option value="">—</option>
            {bookingKinds.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
        </Field>

        {/* Ben, 2026-09-07: the activity may later carry specific lessons from set syllabi. Nothing
            is built for that here and nothing here blocks it — it stays a plain FK to
            TrainingActivity, now editable at Admin → Booking activities. */}
        <Field label="Activity (eg lesson #, topic etc). Optional">
          <select value={form.activity_id} onChange={(e) => upd('activity_id', e.target.value)} style={inputStyle}>
            <option value="">—</option>
            {activities.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>

        {/* ── Participants: instructor picker (S30) ──────────────────────────── */}
        <Field label={`Instructors${rankAllowed ? ' (ranked)' : ''}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {instructorRows.map(({ p, i }) => (
              <div key={`i-${i}`} style={chipRow}>
                {rankAllowed && (
                  <input
                    type="number" min={1} value={p.rank ?? ''} placeholder="#" title="Rank (optional; blank = co-equal)"
                    onChange={(e) => setRankAt(i, e.target.value ? Math.max(1, Number(e.target.value)) : null)}
                    style={{ ...inputStyle, width: 38, padding: '4px 4px', textAlign: 'center' }}
                  />
                )}
                <span style={chipText}>{partLabel(p)}</span>
                {p.user_id == null && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: C.purple, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>External</span>
                )}
                <button type="button" onClick={() => removeAt(i)} title="Remove" style={chipX}>✕</button>
              </div>
            ))}
            <PersonCombobox
              placeholder="Search instructors…"
              options={instructorOptions}
              onSelect={addInstructor}
              extraOptions={EXTERNAL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              // "Create new" makes a STAFF account → admin-only (the full staff form). Non-admins
              // get search + the external options only.
              onCreateNew={isAdmin ? () => setCreateFlow({ kind: 'instructor' }) : undefined}
            />
          </div>
        </Field>

        <div style={sectionHdr}>Booking assets</div>

        {/* Aircraft — fleet + external (cross-hire / client supplied) merged into one picker.
            External rows are free text; they never touch maintenance/serviceability/fleet approval;
            crosshire may carry an invoicer. (UI relocation only — payload/model unchanged.) */}
        <Field label="Aircraft">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {selectedAircraft.map((a) => (
              <div key={a.registration} style={chipRow}>
                <span style={chipText}>{acLabel(a)}</span>
                <button type="button" onClick={() => setAircraftRegs((rs) => rs.filter((r) => r !== a.registration))} title="Remove" style={chipX}>✕</button>
              </div>
            ))}
            {externalAc.map((e, idx) => (
              <div key={`ext-${idx}`} style={{ ...chipRow, flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{EXTERNAL_AC_LABELS[e.source]}</span>
                  <button type="button" onClick={() => setExternalAc((rs) => rs.filter((_, i) => i !== idx))} title="Remove" style={{ ...chipX, marginLeft: 'auto' }}>✕</button>
                </div>
                <input value={e.type} maxLength={50} placeholder="Type (e.g. C172)"
                  onChange={(ev) => setExternalAc((rs) => rs.map((r, i) => (i === idx ? { ...r, type: ev.target.value } : r)))} style={inputStyle} />
                <input value={e.registration} maxLength={10} placeholder="Registration (e.g. VH-ABC)"
                  onChange={(ev) => setExternalAc((rs) => rs.map((r, i) => (i === idx ? { ...r, registration: ev.target.value } : r)))} style={inputStyle} />
                {e.source === 'crosshire' && (
                  <input value={e.invoicer} maxLength={120} placeholder="Invoicer (optional)"
                    onChange={(ev) => setExternalAc((rs) => rs.map((r, i) => (i === idx ? { ...r, invoicer: ev.target.value } : r)))} style={inputStyle} />
                )}
              </div>
            ))}
            <select value="" onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              if (v === 'ext:crosshire' || v === 'ext:client_supplied') {
                const source = (v === 'ext:crosshire' ? 'crosshire' : 'client_supplied') as ExternalAircraftSource;
                setExternalAc((rs) => [...rs, { source, type: '', registration: '', invoicer: '' }]);
              } else {
                setAircraftRegs((rs) => (rs.includes(v) ? rs : [...rs, v]));
              }
            }} style={inputStyle}>
              <option value="">{selectedAircraft.length || externalAc.length ? '+ Add aircraft…' : 'No aircraft'}</option>
              {addableAircraft.map((a) => <option key={a.registration} value={a.registration}>{acLabel(a)}</option>)}
              <option value="ext:crosshire">＋ Cross-hire aircraft…</option>
              <option value="ext:client_supplied">＋ Client-supplied aircraft…</option>
            </select>
          </div>
        </Field>

        <Field label="Classrooms / Simulator">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {selectedAssets.map((a) => (
              <div key={a.id} style={chipRow}>
                <span style={chipText}>{a.name}{a.category ? ` — ${a.category}` : ''}</span>
                <button type="button" onClick={() => setAssetIds((ids) => ids.filter((id) => id !== a.id))} title="Remove" style={chipX}>✕</button>
              </div>
            ))}
            <select value="" onChange={(e) => { const v = e.target.value; if (v) setAssetIds((ids) => (ids.includes(v) ? ids : [...ids, v])); }} style={inputStyle}>
              <option value="">{selectedAssets.length ? '+ Add another…' : 'No classroom / simulator'}</option>
              {addableAssets.map((a) => <option key={a.id} value={a.id}>{a.name}{a.category ? ` — ${a.category}` : ''}</option>)}
            </select>
          </div>
        </Field>

        <div style={sectionHdr}>Group Booking Settings</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '0 12px', alignItems: 'end' }}>
          <Field label="Participant limit">
            <input type="number" min={1} value={maxParticipants} placeholder="No cap" onChange={(e) => setMaxParticipants(e.target.value)} style={inputStyle} />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textDim, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={hardLimit} onChange={(e) => setHardLimit(e.target.checked)} disabled={!maxParticipants.trim()} style={{ accentColor: C.accent }} />
            Fixed group (hard limit — additions beyond the cap are rejected)
          </label>
        </div>

        <Field label="Notes">
          <textarea value={form.notes} onChange={(e) => upd('notes', e.target.value)} placeholder="Student briefing notes, special requirements, hour building details…" style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
        </Field>

        {/* Staff-only note + the instructor reminder. Boxed and slate-tinted so it can never be
            mistaken at a glance for the shared Notes field above it — the two write to different
            columns and are read by different audiences.
            AMENDED 2026-08-25: the reminder is now CONTENT GATED, so the checkbox no longer arms
            it. A reminder goes out automatically when the booking has a staff note or an
            outstanding approval; the checkbox is the OVERRIDE for sending one anyway. */}
        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.borderMed}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.slate, marginBottom: 2 }}>🔒 Internal note (staff only)</div>
          <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 6, lineHeight: 1.45 }}>
            Not shown to students. Use this for staff instructions and reminders.
          </div>
          <textarea
            value={form.internal_note}
            onChange={(e) => upd('internal_note', e.target.value)}
            placeholder="Kit to hand over, paperwork to bring, anything the instructor needs to know."
            style={{ ...inputStyle, minHeight: 60, resize: 'vertical', background: C.surface }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textDim, cursor: 'pointer', marginTop: 8 }}>
            <input type="checkbox" checked={remindInstructor} onChange={(e) => setRemindInstructor(e.target.checked)} style={{ accentColor: C.accent }} />
            Always remind the instructor
          </label>
          <div style={{ fontSize: 10, color: C.textMuted, marginTop: 3, marginLeft: 20, lineHeight: 1.45 }}>
            A reminder is sent automatically when there is a staff note or an approval outstanding.
            Tick this to send one regardless.
          </div>
        </div>

        {isMaintenance && aircraftRegs.length > 0 && (
          <div style={{ background: '#eff6ff', border: `1px solid ${C.accentBorder}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginBottom: 8 }}>🔧 Maintenance — {selectedKind?.name}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '0 12px' }}>
              <Field label="Confirmed with"><input value={maintWith} onChange={(e) => setMaintWith(e.target.value)} placeholder="e.g. Rick Jones" style={inputStyle} /></Field>
              <Field label="Confirmed on"><input type="date" value={maintDate} onChange={(e) => setMaintDate(e.target.value)} style={inputStyle} /></Field>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textDim, cursor: 'pointer', marginTop: 2 }}>
              <input type="checkbox" checked={markUnserviceable} onChange={(e) => setMarkUnserviceable(e.target.checked)} style={{ accentColor: C.accent }} />
              Mark {aircraftRegs.join(', ')} unserviceable until confirmed back (ECD = finish)
            </label>
          </div>
        )}

        {crewNeedingApproval.length > 0 &&
          hint(`⚙ ${crewNeedingApproval.map((s) => staffLabel(s)).join(', ')} ${crewNeedingApproval.length > 1 ? 'are' : 'is'} not automatically available — a booking request will need approval. Students are not notified until approved.`, C.amber, C.amberLight, C.amberBorder)}
        {acsNeedingApproval.map((ac) =>
          <div key={`acappr-${ac.registration}`}>
            {hint(`${ac.availability_mode === 'third_party' ? '👤 Owner approval' : '🔗 Charter availability'} required for ${ac.registration}. Students are not notified until approved.`,
              ac.availability_mode === 'third_party' ? C.purple : C.amber,
              ac.availability_mode === 'third_party' ? '#faf5ff' : C.amberLight,
              ac.availability_mode === 'third_party' ? '#ddd6fe' : C.amberBorder)}
          </div>)}
        {assetsNeedingConfirm.map((as) =>
          <div key={`asconf-${as.id}`}>
            {hint(`${as.name} ${as.ownership === 'external' ? 'is externally owned' : as.ownership === 'shared' ? 'is shared' : as.ppr_required ? 'requires PPR' : 'requires booking confirmation'} — record how it was confirmed once booked.`, C.amber, C.amberLight, C.amberBorder)}
          </div>)}

        {error && hint(`⚠ ${error}`, C.red, C.redLight, C.redBorder)}

        {/* ── §B double-booking hard-block prompt + PR-000023 external-overlap soft challenge ── */}
        {collision && (() => {
          // external_overlap (PR-000023) is a SOFT, amber "book anyway" challenge on the same
          // external rego — NOT a hard §B block; it offers no waitlist path.
          const isExternal = collision.reason === 'external_overlap';
          const isPending = collision.reason === 'pending_displacement';
          // PR-000046: an edited maintenance booking clashing with confirmed booking(s) —
          // maintenance wins; confirming bumps the listed bookings to the waitlist.
          const isMaintWins = collision.reason === 'maintenance_displacement';
          const boxBg = isExternal ? C.amberLight : C.redLight;
          const boxBorder = isExternal ? C.amberBorder : C.redBorder;
          const headColor = isExternal ? C.amber : C.red;
          return (
          <div style={{ background: boxBg, border: `1px solid ${boxBorder}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: headColor, marginBottom: 6 }}>
              {isExternal ? '⚠ Aircraft already booked' : isMaintWins ? '⛔ Maintenance clashes with confirmed booking(s)' : isPending ? '⚠ Slot holds a pending booking' : '⛔ Double-booking'}
            </div>
            <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.5, marginBottom: 6 }}>
              {isExternal
                ? 'A booking already exists on this aircraft:'
                : isMaintWins
                  ? 'This maintenance now overlaps:'
                  : isPending
                    ? 'This slot holds a pending booking:'
                    : 'This time is already taken:'}
            </div>
            {/* PR-000027: one row per clash — resource line, the clashing booking's title, and its
                local date + start–end time (base tz). Participant names stay server-redacted. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
              {dedupeCollisions(collision.collisions).map((c, i) => {
                const tz = base?.timezone ?? 'Australia/Darwin';
                const when = c.block_start_at
                  ? `${dateKeyInTz(c.block_start_at, tz)} · ${fmtTimeInTz(c.block_start_at, tz)}${c.block_end ? `–${fmtTimeInTz(c.block_end, tz)}` : ''}`
                  : '';
                return (
                  <div key={`${c.booking_id}-${c.line.label}-${i}`} style={{ fontSize: 11, color: C.textDim, background: C.surface, border: `1px solid ${boxBorder}`, borderRadius: 5, padding: '5px 8px' }}>
                    <span><strong style={{ color: C.text }}>{c.line.label}</strong> — {c.title ?? '(untitled)'}</span>
                    {when && <div style={{ color: C.textMuted, marginTop: 1 }}>{when}</div>}
                  </div>
                );
              })}
            </div>
            {!isExternal && (
              <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.5, marginBottom: 8 }}>
                {isMaintWins
                  ? `The aircraft is unserviceable for this maintenance, so continuing will move ${collision.collisions.length === 1 ? 'this confirmed booking' : `these ${collision.collisions.length} confirmed bookings`} to the waitlist for re-allocation. Continue?`
                  : isPending
                    ? 'Confirm yours and move the pending booking to the waitlist?'
                    : 'It can be saved to the waitlist, where it holds your requested time until the slot frees.'}
              </div>
            )}
            {isPending && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', marginBottom: 3 }}>NOTIFY THE DISPLACED BOOKING</div>
                <select value={displaceNotify} onChange={(e) => setDisplaceNotify(e.target.value as typeof displaceNotify)} style={inputStyle}>
                  <option value="none">Don't notify</option>
                  <option value="instructor">Notify its instructor</option>
                  <option value="student">Notify its student(s)</option>
                  <option value="both">Notify both</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setCollision(null)} disabled={submitting} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '8px', fontSize: 11, cursor: 'pointer' }}>Back</button>
              {isMaintWins ? (
                <button onClick={() => { setCollision(null); submit({ displace_confirmed: true }); }} disabled={submitting} style={{ flex: 2, background: C.red, border: 'none', color: '#fff', borderRadius: 6, padding: '8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Continue &amp; move to waitlist</button>
              ) : isPending ? (
                <button onClick={() => { setCollision(null); submit({ displace_pending: true, displace_notify: displaceNotify }); }} disabled={submitting} style={{ flex: 2, background: C.amber, border: 'none', color: '#fff', borderRadius: 6, padding: '8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Confirm &amp; move pending to waitlist</button>
              ) : isExternal ? (
                <button onClick={() => { setCollision(null); submit({ ack_external_overlap: true }); }} disabled={submitting} style={{ flex: 2, background: C.amber, border: 'none', color: '#fff', borderRadius: 6, padding: '8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Book anyway</button>
              ) : (
                <button onClick={() => { setCollision(null); submit({ save_as_waitlist: true }); }} disabled={submitting} style={{ flex: 2, background: C.amber, border: 'none', color: '#fff', borderRadius: 6, padding: '8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Save to waitlist</button>
              )}
            </div>
          </div>
          );
        })()}

        {/* ── PR-000010 soft challenge — no student/client nominated (confirm, don't block) ── */}
        {softConfirm && (
          <div style={{ background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 6 }}>⚠ No student or client</div>
            <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.5, marginBottom: 8 }}>{softConfirm.message}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setSoftConfirm(null)} disabled={submitting} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '8px', fontSize: 11, cursor: 'pointer' }}>Go back</button>
              <button type="button" onClick={() => { void submit(softConfirm.extra, true); }} disabled={submitting} style={{ flex: 2, background: C.amber, border: 'none', color: '#fff', borderRadius: 6, padding: '8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Proceed anyway</button>
            </div>
          </div>
        )}

        {/* ── 5d-p2b offline overlap prompt (advisory vs cached data — NOT §B) ── */}
        {offlinePrompt && (
          <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 6 }}>⌛ Offline — possible overlap on {offlinePrompt.overlapLabels.join(', ')}</div>
            <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.5, marginBottom: 8 }}>
              Your last-loaded data shows another booking in this slot. Availability can only be
              confirmed at sync — choose how to queue this one.
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => setOfflinePrompt(null)} disabled={submitting} style={{ flex: 1, minWidth: 80, background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '8px', fontSize: 11, cursor: 'pointer' }}>Go back</button>
              <button onClick={() => { void queueOfflineCreate(offlinePrompt.payload, false); }} disabled={submitting} style={{ flex: 2, minWidth: 140, background: '#7c3aed', border: 'none', color: '#fff', borderRadius: 6, padding: '8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Queue anyway — conflict decided at sync</button>
              <button onClick={() => { void queueOfflineCreate(offlinePrompt.payload, true); }} disabled={submitting} style={{ flex: 2, minWidth: 140, background: C.amber, border: 'none', color: '#fff', borderRadius: 6, padding: '8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Queue as waitlist join</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={props.onClose} disabled={submitting} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '9px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => submit()} disabled={submitting} style={{ flex: 2, background: C.navy, border: 'none', color: '#fff', borderRadius: 6, padding: '9px', fontSize: 12, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Saving…' : repeat ? 'Create recurring' : editing ? 'Save Changes' : 'Save Booking'}
          </button>
        </div>
      </div>
    </ModalOverlay>

    {/* Inline "Create new" (Phase 5). Student/client → trimmed modal (all staff), prefilled from
        the typed text; on the duplicate prompt the operator can pick the existing person. Instructor
        → full staff form (admin-only). A created person is added to the booking immediately. */}
    {createFlow?.kind === 'person' && (
      <StudentClientCreateModal
        prefill={createFlow.prefill}
        onClose={() => setCreateFlow(null)}
        onSaved={(created) => { if (created) addCreatedParticipant(created.id, created.label, created.role === 'client' ? 'client' : 'student'); else setCreateFlow(null); }}
        onPickExisting={(id) => { addStudent(id); setCreateFlow(null); }}
      />
    )}
    {createFlow?.kind === 'instructor' && (
      <UserFormModal
        mode="create"
        baseOptions={baseOptions}
        onClose={() => setCreateFlow(null)}
        onSaved={(created) => { if (created) addCreatedParticipant(created.id, created.label, 'instructor'); else setCreateFlow(null); }}
      />
    )}
    </>
  );
}
